import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { faqData } from "./faqData";

export function FAQTopicPage() {
  const { topicId } = useParams();

  const topicData = faqData[topicId];

  if (!topicData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Topic Not Found
          </h1>
          <p className="text-muted-foreground mb-6">
            The FAQ topic you&apos;re looking for doesn&apos;t exist.
          </p>
          <Link to="/docs">
            <Button>Back to Help Center</Button>
          </Link>
        </div>
      </div>
    );
  }

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const staggerItems = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-background via-purple-950/10 dark:via-purple-950/20 to-background pointer-events-none z-0" />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 overflow-hidden z-10">
        {/* Floating Gradient Orb */}
        <motion.div
          className="absolute top-1/4 right-1/4 w-48 h-48 rounded-full bg-gradient-to-r from-purple-600/15 to-blue-500/15 blur-3xl"
          animate={{
            x: [0, 20, 0],
            y: [0, -20, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Hero Content */}
        <div className="container relative z-10 mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Back Button */}
            <Link
              to="/docs"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Help Center
            </Link>

            {/* Title and Description */}
            <h1 className="text-3xl md:text-5xl font-bold mb-4 pb-2 md:pb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500 dark:from-purple-400 dark:to-blue-400">
              {topicData.title}
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl">
              {topicData.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content Section */}
      <section className="relative z-10 pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            className="rounded-2xl border border-border/50 dark:border-border/30 shadow-lg bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Accordion
              type="single"
              collapsible
              className="w-full [&_[data-radix-accordion-item]]:border-none"
            >
              <motion.div
                variants={staggerItems}
                initial="hidden"
                animate="visible"
              >
                {topicData.questions.map((faq, index) => (
                  <motion.div key={index} variants={fadeIn}>
                    <AccordionItem
                      value={`item-${index}`}
                      className="bg-transparent overflow-hidden mb-3 border-none"
                    >
                      <AccordionTrigger className="text-left font-medium py-4 px-6 bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-xl group flex justify-between text-foreground no-underline hover:no-underline data-[state=open]:no-underline border border-gray-200/50 dark:border-gray-700/50 hover:border-gray-300/50 dark:hover:border-gray-600/50 transition-all">
                        <span className="text-lg font-semibold no-underline pr-4">
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-4 data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
                        <div className="pt-2 px-6 text-base leading-relaxed">
                          {faq.answer.split("\n").map((paragraph, i) => (
                            <p key={i} className="mb-3 last:mb-0">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </motion.div>
            </Accordion>
          </motion.div>

          {/* Contact Support Section */}
          <motion.div
            className="mt-12 text-center bg-gradient-to-r from-purple-600/10 to-blue-600/10 dark:from-purple-400/10 dark:to-blue-400/10 rounded-2xl p-8 border border-purple-200/20 dark:border-purple-700/20 backdrop-blur-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              Still need help?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Can&apos;t find what you&apos;re looking for? Our support team is
              here to help you get the most out of Artifact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/support"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white font-medium rounded-full shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all duration-300"
              >
                Contact Support
              </Link>
              <Link
                to="/docs"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-full hover:bg-gray-50/50 dark:hover:bg-gray-800/50 backdrop-blur-sm transition-all duration-300"
              >
                Back to Documentation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default FAQTopicPage;
